import { addEventProcessor, captureSession, getCurrentScope, init } from "@viagogo/cx-sentry";
import type { Event, EventHint } from "@viagogo/cx-sentry";
import type { AppContext } from "src/shared/context/AppContext";
import { isProd } from "src/shared/utils/envUtils";
import { IGNORE_ERRORS_REGEXES } from "./constants";
import { applySamplingRules } from "./errorSampling";
import { fetchFailureEventProcessor } from "./eventProcessors/fetchFailureEventProcessor";
import { googleSignInEventProcessor } from "./eventProcessors/googleSignInEventProcessor";
import { mapboxContainerEventProcessor } from "./eventProcessors/mapboxContainerEventProcessor";
import { getSentryReleaseName, isSentrySupportedEnv } from "./utils";

/**
 * Handles crashes from VIAGOGOAPP error boundaries by managing Sentry session state and event classification.
 *
 * Why this is needed:
 * - By default, React top level page error boundary errors are treated as "handled" exceptions in Sentry
 * - This means they don't count towards crash session rates, which underreports app stability
 * - Our VIAGOGOAPP components represent critical user journeys (ticket purchasing, choosing an event, etc)
 * - When these fail, it's equivalent to an app crash from a user experience perspective
 * - We need accurate crash session rate metrics for monitoring app health and SLAs
 *
 * When React error boundaries catch errors from our app components (tagged with "VIAGOGOAPP-"),
 * this function:
 * 1. Closes the current session as "crashed" to track crash rates in Sentry
 * 2. Captures/sends the crash session data immediately
 * 3. Marks the exception as unhandled and fatal to distinguish from regular component errors
 *
 * This enables proper crash session rate tracking while allowing users to continue browsing
 * after encountering an error boundary.
 */
const handleViagogoAppErrorBoundaryCrash = (event: Event) => {
  // Early return if not a VIAGOGOAPP error boundary crash
  const errorBoundary = event.tags?.["errorBoundary"];
  if (
    typeof errorBoundary !== "string" ||
    !errorBoundary.startsWith("VIAGOGOAPP-") ||
    !event.exception?.values?.length
  ) {
    return;
  }

  const scope = getCurrentScope();
  const sess = scope?.getSession?.();
  if (sess) {
    sess.status = "crashed";
    // end & send the session update in one go
    captureSession(true);
  }

  // Mark all exception values as unhandled crashes
  for (const v of event.exception.values) {
    v.mechanism = { type: "generic", ...v.mechanism, handled: false }; // treat as crash-like to distinguish from component boundary errors
  }

  event.level = "fatal";
};

export interface SentryRuntimeConfig {
  release: string;
  environment: string;
  sampleRate: number;
  enabled: boolean;
}

// appContext is optional here — it's only used as Sentry.init()'s isolation-scope hint, not
// for any derivation (the caller already supplied every field in config).
const configureSentry = (config: SentryRuntimeConfig, appContext?: AppContext) => {
  init(
    {
      enabled: config.enabled, // captures sentry events/transactions but doesn't send it
      release: config.release,
      dsn: "https://f06e26bd56d210c320bdcaa2ecda962a@o4507805351280640.ingest.us.sentry.io/4507805362356224",
      environment: config.environment,
      ignoreErrors: IGNORE_ERRORS_REGEXES,
      sampleRate: config.sampleRate,
      beforeSend: (event: Event, hint: EventHint) => {
        // surface error in js console during development/qa since won't be sent to Sentry
        if (!config.enabled) {
          console.error(hint.originalException || hint.syntheticException);
        }
        handleViagogoAppErrorBoundaryCrash(event);

        // Apply sampling rules to reduce high-volume errors
        return applySamplingRules(event);
      },
    },
    appContext,
  );

  addEventProcessor(fetchFailureEventProcessor);
  addEventProcessor(googleSignInEventProcessor);
  addEventProcessor(mapboxContainerEventProcessor);
};

export const initSentry = (appContext: AppContext) =>
  configureSentry(
    {
      enabled: isSentrySupportedEnv(appContext.env),
      release: getSentryReleaseName(appContext),
      environment: isProd(appContext.env) ? "production" : "development",
      // TODO: config based so we don't need to deploy to update
      sampleRate: Number(appContext.env["REACT_APP_SENTRY_ERROR_SAMPLE_RATE"] ?? "0"),
    },
    appContext,
  );

// For callers that already know every value (e.g. the SSR app, computed server-side) —
// skips all derivation, so no AppContext needs to be faked to satisfy this function.
export const initSentryWithConfig = (config: SentryRuntimeConfig) => configureSentry(config);
